import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import CreativeCommons from '@wix/da-ds/pkg/Icons/16x16/CreativeCommons';
import Attribution from '@wix/da-ds/pkg/Icons/16x16/Attribution';
import NonCommercial from '@wix/da-ds/pkg/Icons/16x16/NonCommercial';
import NoDerivatives from '@wix/da-ds/pkg/Icons/16x16/NoDerivatives';
import ShareAlike from '@wix/da-ds/pkg/Icons/16x16/ShareAlike';
import UserLink from '../User/Link/UserLink';

import s from './License.scss';

export interface Props {
  license?: string;
  className?: string;
  publishedTime?: string;
  author?: PapiUser;
  showSymbol?: boolean;
  showIcons?: boolean;
}

export const License: React.FC<Props> = ({
  license = 'none',
  className,
  publishedTime,
  author,
  showSymbol = true,
  showIcons = false,
}) => {
  const { t } = useTranslation();
  const publishedYear = publishedTime && new Date(publishedTime).getFullYear();
  const currentYear = new Date().getFullYear();
  const years =
    !publishedYear || publishedYear === currentYear
      ? currentYear
      : `${publishedYear} - ${currentYear}`;

  let content;
  if (license === 'none') {
    content = (
      <>
        {` © ${years} `}
        {author && (
          <UserLink
            user={author}
            showSymbol={showSymbol}
          />
        )}
      </>
    );
  } else {
    const iconsByLicense = {
      by: [CreativeCommons, Attribution],
      'by-sa': [CreativeCommons, Attribution, ShareAlike],
      'by-nd': [CreativeCommons, Attribution, NoDerivatives],
      'by-nc': [CreativeCommons, Attribution, NonCommercial],
      'by-nc-sa': [CreativeCommons, Attribution, NonCommercial, ShareAlike],
      'by-nc-nd': [CreativeCommons, Attribution, NonCommercial, NoDerivatives],
    };

    content = (
      <>
        {showIcons && iconsByLicense.hasOwnProperty(license) && (
          <div className={s['license-icons']}>
            {iconsByLicense[license].map((Icon, idx) => (
              <Icon
                key={`icon-cc-${idx}`}
                size={IconSize.SMALLER}
                disableDefaultColors
              />
            ))}
          </div>
        )}
        <a
          rel="license noopener noreferrer"
          target="_blank"
          href={`http://creativecommons.org/licenses/${license}/3.0/`}
        >
          {t(`deviation.details.license.${license}`)}
        </a>
      </>
    );
  }

  if (className) {
    return <div className={className}>{content}</div>;
  } else {
    return content;
  }
};
License.displayName = 'License';

export default License;
